import { ChangeEvent, FC, Suspense, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import SaveIcon from "@mui/icons-material/Save";
import StackedLineChartIcon from "@mui/icons-material/StackedLineChart";
import {
	Box,
	ButtonGroup,
	Chip,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { green, grey, red } from "@mui/material/colors";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { Translate } from "@/components/Application/Translate";
import { MeasurementsHomePage } from "@/components/Measurements/Pages/MeasurementsHomePage";
import { MeasurementPoint } from "@/components/Measurements/Types/MeasurementGroups";

import { ErrorToast } from "@/helpers/alerts/AlertToast";
import StyledFullModal from "@/helpers/components/StyledFullModal";

import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";
import { UpdateProcedureDetailStepStatusRequest } from "@/model/Procedure/UpdateProcedureDetailStepStatusRequest";

import { UpdateProcedureDetailStepState } from "@/service/ProcedureMutations";
import { useFetchActiveChecklistStatus } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

dayjs.extend(utc);

type Props = {
	step: ProcedureNodeStep;
	activeSchemeId: number | undefined;
	isDisabled?: boolean;
};

export const StepManualMeasurements: FC<Props> = ({
	step,
	activeSchemeId,
	isDisabled,
}) => {
	let { id } = useParams<RouteParams>();
	const [loading, setLoading] = useState<boolean>(false);
	const [saveStateSuccess, setSaveStateSuccess] = useState(true);
	const [manualRegistration, setManualRegistration] = useState<
		number | undefined
	>();
	const [measurementModalOpen, toggleMeasurementModalOpen] =
		useState<boolean>(false);

	const { data: stepStatuses } = useFetchActiveChecklistStatus(activeSchemeId);
	const currentStepStatus = stepStatuses?.find((x) => x.Id === step.Id);

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (!value || value === "") {
			setManualRegistration(undefined);
			return;
		}
		if (/^-?\d*\.?\d*$/.test(value)) {
			setManualRegistration(Number(value));
		} else {
			console.log("Invalid number entered:", value);
			ErrorToast(
				<Translate String="Procedure.Checklist.ManualReading.ValueField.Error">
					Please enter a valid number.
				</Translate>,
			);
		}
	};

	const updateProcedureDetailStepState = UpdateProcedureDetailStepState();

	const Save = async () => {
		setLoading(true);
		try {
			const request: UpdateProcedureDetailStepStatusRequest = {
				ArchiveType: "Measurement",
				MeasurementId: step.ManualReading?.Id,
				ProcedureId: parseInt(id),
				SchemeId: activeSchemeId,
				Status: 1,
				StepId: step.Id,
				Value: manualRegistration,
			};
			await updateProcedureDetailStepState(request);

			setSaveStateSuccess(true);
		} catch (error: unknown) {
			setSaveStateSuccess(false);
			ErrorToast(error.message);
		} finally {
			setLoading(false);
		}
	};

	useMemo(() => {
		if (!currentStepStatus?.Status) {
			setSaveStateSuccess(false);
		} else if (currentStepStatus?.ManualMeasurementValue) {
			setManualRegistration(+currentStepStatus?.ManualMeasurementValue);
			setSaveStateSuccess(true);
		}
	}, [step?.ManualReadingValues, currentStepStatus?.Status]);

	if (!step?.ManualReading) {
		return <></>;
	}

	const initialManualReading: MeasurementPoint = {
		Id: step.ManualReading.MeasurementPointId,
		Name: step.ManualReading.Name,
	};

	return (
		<Box>
			<Chip
				sx={{
					height: "130px",
					backgroundColor: saveStateSuccess
						? green[300]
						: !manualRegistration
							? grey[200]
							: red[400],
					boxShadow: 2, // Replacing elevation with boxShadow
				}}
				label={
					<Box display="flex" flexDirection="column" gap={2} p={2}>
						<Typography>{step.ManualReading?.Name}</Typography>
						<TextField
							sx={{ alignSelf: "center", width: "100%" }}
							size="small"
							focused
							disabled={isDisabled}
							name={`${step.ManualReading.MeasurementPointId}`}
							value={manualRegistration === undefined ? "" : manualRegistration}
							inputProps={{
								inputMode: "decimal",
								pattern: "[0-9]*[.,]?[0-9]*",
							}}
							onKeyDown={(e) => {
								// Allow: backspace, delete, tab, escape, enter, period, comma, arrows
								if (
									[
										"Backspace",
										"Delete",
										"Tab",
										"Escape",
										"Enter",
										"ArrowLeft",
										"ArrowRight",
										".",
										",",
										"-",
									].includes(e.key)
								) {
									return;
								}
								// Block if not a number
								if (!/^[0-9]$/.test(e.key)) {
									e.preventDefault();
								}
							}}
							label={
								<Translate String="Procedure.Checklist.ManualReading.ValueField">
									Value
								</Translate>
							}
							onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e)}
						/>
						<Typography variant="caption" color="textSecondary">
							<Translate String="Procedure.Checklist.ManualReading.ValueRange">
								Range: {step.ManualReading.Min} - {step.ManualReading.Max}
							</Translate>
						</Typography>
					</Box>
				}
			/>
			<ButtonGroup orientation="vertical" sx={{ ml: 2 }}>
				<Tooltip
					title={
						<Translate String="Checklist.Measurement.Page.Button.ToolTip">
							Measurement page
						</Translate>
					}
					placement="top"
				>
					<IconButton
						color="info"
						onClick={() => toggleMeasurementModalOpen(true)}
					>
						<StackedLineChartIcon />
					</IconButton>
				</Tooltip>
				<Tooltip
					title={
						<Translate String="Checklist.Measurement.Save.Button.ToolTip">
							Save
						</Translate>
					}
				>
					<IconButton
						color="success"
						onClick={() => Save()}
						disabled={saveStateSuccess}
					>
						<SaveIcon />
					</IconButton>
				</Tooltip>
			</ButtonGroup>
			<StyledFullModal
				open={measurementModalOpen}
				onClose={() => toggleMeasurementModalOpen(false)}
				title={step.ManualReading?.Name}
				isFullScreen={false}
			>
				<Suspense fallback={null}>
					<MeasurementsHomePage
						initialSelectedMeasurement={initialManualReading}
					/>
				</Suspense>
			</StyledFullModal>
		</Box>
	);
};
